import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import LoginPage from './pages/Login';
import DashboardPage from './pages/Dashboard';
import InspectionDetailPage from './pages/InspectionDetail';
import InspectionVerdictPage from './pages/InspectionVerdict';
import AdminPage from './pages/Admin';
import { useAuthContext, AuthProvider } from './context/AuthContext';
import './App.css';
import { SpinnerProvider } from './context/SpinnerContext';
import Spinner from './components/Spinner';
import GenerateReportPage from './pages/GenerateReport';
import { ROLES } from './utils/constants';
import ValidateInspectionPage from './pages/ValidateInspection';
import PrivacyPage from './pages/Privacy/PrivacyPage';
import PrivacyNoticeBanner from './components/PrivacyNotice/PrivacyNoticeBanner';

const publicRoutes = ['/login', '/validar-inspeccion', '/privacidad'];

const isPublicRoute = (pathname) => {
  return publicRoutes.some(route => pathname.startsWith(route));
};

const AppContent = () => {
  const { isAuthenticated, loading, currentUser } = useAuthContext();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (isPublicRoute(location.pathname)) {
      return;
    }

    const timeoutId = setTimeout(() => {
      if (loading) {
        const redirectData = {
          path: location.pathname,
          timestamp: Date.now()
        };
        localStorage.setItem('redirectAfterLogin', JSON.stringify(redirectData));
        navigate('/login');
      }
    }, 10000);

    if (!loading) {
      if (!isAuthenticated && location.pathname !== '/login') {
        const redirectData = {
          path: location.pathname,
          timestamp: Date.now()
        };
        localStorage.setItem('redirectAfterLogin', JSON.stringify(redirectData));
        navigate('/login');
      } else if (isAuthenticated && location.pathname === '/login') {
        const redirectDataStr = localStorage.getItem('redirectAfterLogin');

        if (redirectDataStr) {
          try {
            const redirectData = JSON.parse(redirectDataStr);
            const fiveMinutes = 5 * 60 * 1000;
            const age = Date.now() - redirectData.timestamp;
            const isExpired = age > fiveMinutes;

            if (!isExpired && redirectData.path && redirectData.path !== '/login') {
              localStorage.removeItem('redirectAfterLogin');

              setTimeout(() => {
                navigate(redirectData.path, { replace: true });
              }, 100);

              return;
            } else {
              localStorage.removeItem('redirectAfterLogin');
              navigate('/', { replace: true });
            }
          } catch (err) {
            localStorage.removeItem('redirectAfterLogin');
            navigate('/', { replace: true });
          }
        } else {
          navigate('/', { replace: true });
        }
      }
    }

    return () => clearTimeout(timeoutId);
  }, [isAuthenticated, loading, location.pathname, navigate]);

  const isAdministrator = () => {
    return currentUser?.role === ROLES.ADMINISTRATOR;
  };

  if (loading && !isPublicRoute(location.pathname)) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner"></div>
        <p>Cargando...</p>
      </div>
    );
  }

  return (
    <div className="app">
      <Routes>
        <Route
          path="/login"
          element={isAuthenticated ? <Navigate to="/" replace /> : <LoginPage />}
        />

        <Route
          path="/"
          element={isAuthenticated ? <DashboardPage /> : <Navigate to="/login" replace />}
        />
        <Route
          path="/inspeccion/:id"
          element={isAuthenticated ? <InspectionDetailPage /> : <Navigate to="/login" replace />}
        />
        <Route
          path="/dictamenes/:id"
          element={isAuthenticated ? <InspectionVerdictPage /> : <Navigate to="/login" replace />}
        />
        <Route
          path="/admin"
          element={isAuthenticated && isAdministrator() ? <AdminPage /> : <Navigate to="/login" replace />}
        />

        <Route
          path="/reportes"
          element={isAuthenticated && isAdministrator() ? <GenerateReportPage /> : <Navigate to="/login" replace />}
        />

        <Route
          path="/validar-inspeccion/:id"
          element={<ValidateInspectionPage />}
        />

        <Route path="/privacidad" element={<PrivacyPage />} />

        <Route
          path="*"
          element={<Navigate to={isAuthenticated ? "/" : "/login"} replace />}
        />
      </Routes>
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <SpinnerProvider>
        <Router>
          <AppContent />
          <PrivacyNoticeBanner />
          <Spinner />
        </Router>
      </SpinnerProvider>
    </AuthProvider>
  );
}

export default App;